import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { createLogger } from '../utils/logger'

// Connects to data-controller="automation-controls"
export default class extends Controller {
  static values = { 
    buildSessionId: String,
    websocketUrl: String
  }
  
  static targets = [
    "startBtn", "pauseBtn", "resumeBtn", "stopBtn"
  ]

  connect() {
    this.logger = createLogger('automation')
    this.logger.info("Automation controls connected for build session:", this.buildSessionIdValue)
    this.initializeWebSocket()
    this.updateConnectionStatus("connected")
    this.setupSettingsModal()
    
    // Restore button states based on current automation state
    const currentState = document.getElementById('automation-state')?.textContent?.toLowerCase()?.trim()
    if (currentState) {
      // Map text states to button states
      const stateMap = {
        'running': 'running',
        'paused': 'paused',
        'idle': 'idle',
        'completed': 'completed',
        'error': 'error',
        'initializing': 'running'
      }
      const mappedState = stateMap[currentState] || 'idle'
      // Delay to ensure DOM is ready
      setTimeout(() => {
        try {
          this.updateButtonStates(mappedState)
        } catch (error) {
          this.logger.error('Error restoring button states:', error)
        }
      }, 100)
    }
  }

  disconnect() {
    this.logger.debug('Automation controls disconnecting')
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    // Clear any pending timeouts or intervals
    if (this.updateTimer) {
      clearTimeout(this.updateTimer)
    }
  }

  // WebSocket Connection
  initializeWebSocket() {
    this.subscription = consumer.subscriptions.create(
      {
        channel: "BuildProgressChannel",
        build_session_id: this.buildSessionIdValue
      },
      {
        connected: () => {
          this.logger.debug("WebSocket connected")
          this.updateConnectionStatus("connected")
          this.requestAutomationStatus()
        },

        disconnected: () => {
          this.logger.debug("WebSocket disconnected")
          this.updateConnectionStatus("disconnected")
        },

        received: (data) => {
          this.handleWebSocketMessage(data)
        }
      }
    )
  }

  handleWebSocketMessage(data) {
    this.logger.debug("Received WebSocket message:", data)
    
    switch (data.type) {
      case 'automation_update':
        this.handleAutomationUpdate(data)
        break
      case 'story_progress':
        this.handleStoryProgress(data)
        break
      case 'automation_started':
        this.handleAutomationStarted(data)
        break
      case 'automation_paused':
        this.handleAutomationPaused(data)
        break
      case 'automation_resumed':
        this.handleAutomationResumed(data)
        break
      case 'automation_stopped':
        this.handleAutomationStopped(data)
        break
      case 'automation_completed':
        this.handleAutomationCompleted(data)
        break
      case 'automation_error':
        this.handleAutomationError(data)
        break
      case 'session_health':
        this.handleSessionHealth(data)
        break
      case 'completion_signal':
        this.handleCompletionSignal(data)
        break
      case 'story_started':
        this.handleStoryStarted(data)
        break
      case 'story_completed':
        this.handleStoryCompleted(data)
        break
      default:
        this.logger.debug("Unhandled message type:", data.type)
    }

    this.updateLastUpdateTime()
  }

  // Helper methods for HTTP requests
  getUrl(action) {
    const pathParts = window.location.pathname.split('/')
    const projectSlug = pathParts[2] // app_projects/:slug
    const sessionId = pathParts[4] // build_sessions/:id
    return `/app_projects/${projectSlug}/build_sessions/${sessionId}/${action}`
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      'Accept': 'application/json'
    }
  }

  async refreshExecutionControls() {
    try {
      // Use Turbo to refresh the execution-controls frame
      const frame = document.getElementById('execution-controls')
      if (frame && window.Turbo) {
        // Reload the turbo frame by revisiting its source
        const currentSrc = frame.getAttribute('src') || window.location.pathname
        window.Turbo.visit(currentSrc, { frame: 'execution-controls' })
      }
    } catch (error) {
      this.logger.error('Error refreshing execution controls:', error)
    }
  }

  // Automation Control Actions
  async startAutomation(event) {
    event.preventDefault()
    
    const settings = this.getAutomationSettings()
    
    try {
      this.setButtonLoading(this.startBtnTarget, true)
      this.addActivityMessage("Starting automation...", "info")
      
      const response = await fetch(this.getUrl('start_automation'), {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ options: settings })
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.addActivityMessage("Automation started successfully", "success")
        // Clear loading state first, then update button states
        this.setButtonLoading(this.startBtnTarget, false)
        try {
          this.updateButtonStates("running")
        } catch (buttonError) {
          this.logger.error("Error updating button states:", buttonError)
          this.addActivityMessage("Warning: Button states may not have updated correctly", "warning")
        }
      } else {
        this.addActivityMessage(`Failed to start: ${result.error}`, "error")
        this.setButtonLoading(this.startBtnTarget, false)
      }
    } catch (error) {
      this.logger.error("Error starting automation:", error)
      this.addActivityMessage(`Error starting automation: ${error.message}`, "error")
      // Clear loading state on error
      try {
        this.setButtonLoading(this.startBtnTarget, false)
      } catch (loadingError) {
        this.logger.error("Error clearing loading state:", loadingError)
      }
    }
  }

  async pauseAutomation(event) {
    event.preventDefault()
    
    try {
      this.setButtonLoading(this.pauseBtnTarget, true)
      this.addActivityMessage("Pausing automation...", "info")
      
      const response = await fetch(this.getUrl('pause_automation'), {
        method: 'PATCH',
        headers: this.getHeaders()
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.addActivityMessage("Automation paused", "warning")
        this.updateButtonStates("paused")
      } else {
        this.addActivityMessage(`Failed to pause: ${result.error}`, "error")
      }
    } catch (error) {
      this.logger.error("Error pausing automation:", error)
      this.addActivityMessage("Error pausing automation", "error")
    } finally {
      this.setButtonLoading(this.pauseBtnTarget, false)
    }
  }

  async resumeAutomation(event) {
    event.preventDefault()
    
    try {
      this.setButtonLoading(this.resumeBtnTarget, true)
      this.addActivityMessage("Resuming automation...", "info")
      
      const response = await fetch(this.getUrl('resume_automation'), {
        method: 'PATCH',
        headers: this.getHeaders()
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.addActivityMessage("Automation resumed", "success")
        this.updateButtonStates("running")
      } else {
        this.addActivityMessage(`Failed to resume: ${result.error}`, "error")
      }
    } catch (error) {
      this.logger.error("Error resuming automation:", error)
      this.addActivityMessage("Error resuming automation", "error")
    } finally {
      this.setButtonLoading(this.resumeBtnTarget, false)
    }
  }

  async stopAutomation(event) {
    event.preventDefault()
    
    if (!confirm("Are you sure you want to stop automation? This will halt all story execution.")) {
      return
    }
    
    try {
      this.setButtonLoading(this.stopBtnTarget, true)
      this.addActivityMessage("Stopping automation...", "info")
      
      const response = await fetch(this.getUrl('stop_automation'), {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify({ reason: "User requested stop" })
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.addActivityMessage("Automation stopped", "info")
        this.updateButtonStates("idle")
      } else {
        this.addActivityMessage(`Failed to stop: ${result.error}`, "error")
      }
    } catch (error) {
      this.logger.error("Error stopping automation:", error)
      this.addActivityMessage("Error stopping automation", "error")
    } finally {
      this.setButtonLoading(this.stopBtnTarget, false)
    }
  }

  // Modal and UI Actions

  showAnalytics(event) {
    event.preventDefault()
    this.loadAnalytics()
    document.getElementById('automation-analytics-modal').showModal()
  }

  showSettings(event) {
    event.preventDefault()
    this.loadCurrentSettings()
    document.getElementById('automation-settings-modal').showModal()
  }

  showHealth(event) {
    event.preventDefault()
    this.requestSessionHealth()
  }

  async forceCompleteStory(event) {
    event.preventDefault()
    
    if (!confirm("Force complete the current story? This will mark it as done and move to the next.")) {
      return
    }
    
    try {
      const response = await fetch(`/app_projects/${this.getAppProjectSlug()}/build_sessions/${this.buildSessionIdValue}/force_complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ reason: "Manually forced completion" })
      })
      
      const result = await response.json()
      
      if (result.success) {
        this.addActivityMessage("Story force completed", "warning")
      } else {
        this.addActivityMessage(`Failed to force complete: ${result.error}`, "error")
      }
    } catch (error) {
      this.logger.error("Error force completing story:", error)
      this.addActivityMessage("Error force completing story", "error")
    }
  }

  exportLogs(event) {
    event.preventDefault()
    this.downloadActivityLogs()
  }

  clearActivityFeed(event) {
    event.preventDefault()
    const feed = document.getElementById('activity-feed')
    if (feed) {
      feed.innerHTML = '<div class="text-base-content/60 italic">Activity feed cleared</div>'
    }
  }

  saveSettings(event) {
    event.preventDefault()
    const settings = this.collectSettingsFromModal()
    this.saveAutomationSettings(settings)
    document.getElementById('automation-settings-modal').close()
  }

  // WebSocket Message Handlers
  handleAutomationUpdate(data) {
    if (data.event) {
      this.addActivityMessage(`Automation: ${data.event}`, "info")
    }
    
    if (data.data.automation_state) {
      this.updateAutomationState(data.data.automation_state)
    }
    
    if (data.data.queue_progress) {
      this.updateProgress(data.data.queue_progress)
    }
  }

  handleStoryProgress(data) {
    const status = data.status
    const storyTitle = data.data.story_title || `Story ${data.story_id}`
    
    switch (status) {
      case 'started':
        this.addActivityMessage(`▶️ Started: ${storyTitle}`, "info")
        break
      case 'completed':
        this.addActivityMessage(`✅ Completed: ${storyTitle}`, "success")
        break
      case 'failed':
        this.addActivityMessage(`❌ Failed: ${storyTitle}`, "error")
        break
    }
  }
  
  handleStoryStarted(data) {
    const storyTitle = data.story_title || `Story ${data.story_id}`
    this.addActivityMessage(`▶️ Started: ${storyTitle}`, "info")
    this.logger.info(`Story started: ${storyTitle}`)
  }
  
  handleStoryCompleted(data) {
    const storyTitle = data.story_title || `Story ${data.story_id}`
    this.addActivityMessage(`✅ Completed: ${storyTitle}`, "success")
    this.logger.info(`Story completed: ${storyTitle}`)
  }

  handleAutomationStarted(data) {
    this.addActivityMessage("🚀 Automation started", "success")
    this.updateButtonStates("running")
    if (data.queue_size) {
      this.addActivityMessage(`Queue contains ${data.queue_size} stories`, "info")
    }
  }

  handleAutomationPaused(data) {
    this.addActivityMessage("⏸️ Automation paused", "warning")
    this.updateButtonStates("paused")
  }

  handleAutomationResumed(data) {
    this.addActivityMessage("▶️ Automation resumed", "success")
    this.updateButtonStates("running")
  }

  handleAutomationStopped(data) {
    this.addActivityMessage("⏹️ Automation stopped", "info")
    if (data.reason) {
      this.addActivityMessage(`Reason: ${data.reason}`, "info")
    }
    this.updateButtonStates("idle")
  }

  handleAutomationCompleted(data) {
    this.addActivityMessage("✅ Automation completed", "success")
    if (data.message) {
      this.addActivityMessage(data.message, "info")
    }
    this.updateButtonStates("completed")
    
    // Also update any status displays
    const statusElement = document.getElementById('automation-state')
    if (statusElement) {
      statusElement.textContent = 'Completed'
      statusElement.className = 'text-success font-semibold'
    }
    
    // Refresh the execution controls to update button states server-side
    this.refreshExecutionControls()
  }

  handleAutomationError(data) {
    this.addActivityMessage(`❌ Error: ${data.error.message || data.error}`, "error")
    if (data.error.severity === 'critical') {
      this.updateButtonStates("error")
    }
  }

  handleSessionHealth(data) {
    const health = data.health
    let message = `Health: ${health.status} (${health.health_score}/100)`
    let type = health.health_score > 70 ? "success" : health.health_score > 40 ? "warning" : "error"
    
    this.addActivityMessage(`💓 ${message}`, type)
  }

  handleCompletionSignal(data) {
    const signal = data.signal
    this.addActivityMessage(`🎯 Completion signal: ${signal.signal_type} (confidence: ${(signal.confidence_score * 100).toFixed(0)}%)`, "info")
  }

  // UI Helper Methods
  updateConnectionStatus(status) {
    const indicator = document.getElementById('connection-indicator')
    const text = document.getElementById('connection-text')
    
    if (indicator && text) {
      switch (status) {
        case 'connected':
          indicator.className = 'w-2 h-2 bg-green-500 rounded-full'
          text.textContent = 'Connected'
          break
        case 'disconnected':
          indicator.className = 'w-2 h-2 bg-red-500 rounded-full'
          text.textContent = 'Disconnected'
          break
        default:
          indicator.className = 'w-2 h-2 bg-gray-400 rounded-full'
          text.textContent = 'Connecting...'
      }
    }
  }

  updateLastUpdateTime() {
    const element = document.getElementById('last-update-time')
    if (element) {
      element.textContent = new Date().toLocaleTimeString()
    }
  }

  updateButtonStates(state) {
    // Check if targets exist before accessing them
    if (!this.hasStartBtnTarget || !this.hasPauseBtnTarget || 
        !this.hasResumeBtnTarget || !this.hasStopBtnTarget) {
      this.logger.warn('Some automation button targets are missing, skipping button state update')
      return
    }
    
    this.logger.debug(`Updating button states to: ${state}`)
    
    // Use try-catch to handle potential temporal dead zone issues
    let startBtn, pauseBtn, resumeBtn, stopBtn
    try {
      startBtn = this.startBtnTarget
      pauseBtn = this.pauseBtnTarget
      resumeBtn = this.resumeBtnTarget
      stopBtn = this.stopBtnTarget
    } catch (error) {
      this.logger.error('Error accessing button targets:', error)
      return
    }
    
    // Hide all buttons first
    [pauseBtn, resumeBtn, stopBtn].forEach(btn => {
      btn.style.display = 'none'
      btn.disabled = true
    })
    
    switch (state) {
      case 'running':
        startBtn.disabled = true
        startBtn.style.display = 'none'
        pauseBtn.style.display = 'inline-flex'
        pauseBtn.disabled = false
        stopBtn.style.display = 'inline-flex'
        stopBtn.disabled = false
        break
        
      case 'paused':
        startBtn.disabled = true
        startBtn.style.display = 'none'
        resumeBtn.style.display = 'inline-flex'
        resumeBtn.disabled = false
        stopBtn.style.display = 'inline-flex'
        stopBtn.disabled = false
        break
        
      case 'idle':
      case 'completed':
      case 'error':
        startBtn.disabled = false
        startBtn.style.display = 'inline-flex'
        break
    }
    
    this.updateAutomationState(state)
  }

  updateAutomationState(state) {
    const stateElement = document.getElementById('automation-state')
    if (stateElement) {
      stateElement.textContent = state.charAt(0).toUpperCase() + state.slice(1)
    }
  }

  updateProgress(progress) {
    const progressElement = document.getElementById('queue-progress')
    const progressText = document.getElementById('progress-text')
    
    if (progressElement && progress.progress_percentage !== undefined) {
      progressElement.style.setProperty('--value', progress.progress_percentage)
      progressElement.querySelector('span').textContent = `${Math.round(progress.progress_percentage)}%`
    }
    
    if (progressText && progress.completed !== undefined && progress.total !== undefined) {
      progressText.textContent = `${progress.completed}/${progress.total} completed`
    }
  }

  addActivityMessage(message, type = 'info') {
    const feed = document.getElementById('activity-feed')
    if (!feed) return
    
    const timestamp = new Date().toLocaleTimeString()
    const typeIcon = {
      'info': 'ℹ️',
      'success': '✅',
      'warning': '⚠️',
      'error': '❌'
    }[type] || 'ℹ️'
    
    const messageElement = document.createElement('div')
    messageElement.className = `text-xs flex items-start gap-1 ${type === 'error' ? 'text-error' : type === 'success' ? 'text-success' : type === 'warning' ? 'text-warning' : ''}`
    messageElement.innerHTML = `
      <span class="text-base-content/60">[${timestamp}]</span>
      <span>${typeIcon}</span>
      <span class="flex-1">${message}</span>
    `
    
    // Clear "waiting" message if it exists
    const waitingMessage = feed.querySelector('.italic')
    if (waitingMessage) {
      waitingMessage.remove()
    }
    
    feed.appendChild(messageElement)
    feed.scrollTop = feed.scrollHeight
    
    // Keep only last 50 messages
    while (feed.children.length > 50) {
      feed.removeChild(feed.firstChild)
    }
  }

  setButtonLoading(button, loading) {
    if (loading) {
      // Store original text before changing it
      button.dataset.originalText = button.innerHTML
      button.innerHTML = '<span class="loading loading-spinner loading-xs"></span> Loading...'
      button.disabled = true
    } else {
      // Restore original text or use default
      const originalText = button.dataset.originalText || button.innerHTML
      button.innerHTML = originalText
      button.disabled = false
    }
  }

  // WebSocket Communication
  async sendChannelAction(action, data = {}) {
    return new Promise((resolve, reject) => {
      if (!this.subscription) {
        reject(new Error('WebSocket not connected'))
        return
      }
      
      try {
        const result = this.subscription.perform(action, {
          build_session_id: this.buildSessionIdValue,
          ...data
        })
        resolve(result || { success: true })
      } catch (error) {
        reject(error)
      }
    })
  }

  async requestAutomationStatus() {
    try {
      await this.sendChannelAction("request_automation_status")
    } catch (error) {
      this.logger.error("Error requesting automation status:", error)
    }
  }

  async requestSessionHealth() {
    try {
      await this.sendChannelAction("request_session_health")
    } catch (error) {
      this.logger.error("Error requesting session health:", error)
    }
  }

  async loadAnalytics() {
    try {
      const content = document.getElementById('analytics-content')
      if (!content) {
        // If modal doesn't exist, create it and load content
        const modal = document.createElement('dialog')
        modal.id = 'automation-analytics-modal'
        modal.className = 'modal'
        modal.innerHTML = `
          <div class="modal-box w-11/12 max-w-6xl max-h-[90vh] overflow-y-auto">
            <div class="flex justify-between items-center mb-4">
              <h3 class="font-bold text-lg">Automation Analytics</h3>
              <form method="dialog">
                <button class="btn btn-sm btn-circle btn-ghost">✕</button>
              </form>
            </div>
            <div id="analytics-content">
              <div class="flex justify-center py-8">
                <span class="loading loading-spinner loading-lg"></span>
              </div>
            </div>
          </div>
        `
        document.body.appendChild(modal)
      }
      
      // Load analytics content via HTTP
      const response = await fetch(`${window.location.pathname}/automation_analytics`, {
        headers: {
          'Accept': 'text/html'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        document.getElementById('analytics-content').innerHTML = html
      } else {
        document.getElementById('analytics-content').innerHTML = `
          <div class="alert alert-error">
            <i class="fas fa-exclamation-circle"></i>
            <span>Failed to load analytics data</span>
          </div>
        `
      }
    } catch (error) {
      this.logger.error("Error loading analytics:", error)
      const content = document.getElementById('analytics-content')
      if (content) {
        content.innerHTML = `
          <div class="alert alert-error">
            <i class="fas fa-exclamation-circle"></i>
            <span>Error loading analytics: ${error.message}</span>
          </div>
        `
      }
    }
  }

  // Settings Management
  setupSettingsModal() {
    // Setup tab switching
    document.querySelectorAll('#automation-settings-modal [data-tab]').forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault()
        const tabName = e.target.dataset.tab
        this.switchSettingsTab(tabName)
      })
    })
  }

  switchSettingsTab(tabName) {
    // Update tab buttons
    document.querySelectorAll('#automation-settings-modal .tab').forEach(tab => {
      tab.classList.toggle('tab-active', tab.dataset.tab === tabName)
    })
    
    // Update tab content
    document.querySelectorAll('#automation-settings-modal .tab-content').forEach(content => {
      content.style.display = content.id === `settings-${tabName}` ? 'block' : 'none'
    })
  }

  getAutomationSettings() {
    // Return default settings for now - these would be collected from the settings modal
    return {
      optimize_for_automation: true,
      execution: {
        story_timeout: 30 * 60, // 30 minutes in seconds
        pause_between_stories: 2
      },
      completion_detection: {
        threshold: 0.6,
        idle_timeout: 10 * 60 // 10 minutes in seconds
      },
      failure_handling: {
        strategy: 'skip',
        max_retries: 2
      }
    }
  }

  loadCurrentSettings() {
    // Load current automation settings into the modal
    // This would fetch from the server and populate the form
  }

  collectSettingsFromModal() {
    const settings = {}
    
    document.querySelectorAll('#automation-settings-modal [data-setting]').forEach(input => {
      const key = input.dataset.setting
      let value = input.value
      
      if (input.type === 'checkbox') {
        value = input.checked
      } else if (input.type === 'number' || input.type === 'range') {
        value = parseFloat(value)
      }
      
      this.setNestedValue(settings, key, value)
    })
    
    return settings
  }

  setNestedValue(obj, path, value) {
    const keys = path.split('.')
    let current = obj
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!current[key]) current[key] = {}
      current = current[key]
    }
    
    current[keys[keys.length - 1]] = value
  }

  saveAutomationSettings(settings) {
    // Save settings to the server
    this.logger.debug("Saving automation settings:", settings)
    // This would make an API call to save the settings
  }

  downloadActivityLogs() {
    const feed = document.getElementById('activity-feed')
    if (!feed) return
    
    const logs = Array.from(feed.children).map(child => child.textContent).join('\n')
    const blob = new Blob([logs], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = `automation-logs-${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    
    URL.revokeObjectURL(url)
  }

  // Utility Methods
  getCSRFToken() {
    const token = document.querySelector('[name="csrf-token"]')
    return token ? token.content : ''
  }

  getAppProjectSlug() {
    // Extract from URL or data attribute
    const path = window.location.pathname
    const match = path.match(/\/app_projects\/([^\/]+)/)
    return match ? match[1] : ''
  }

  displayHealthStatus(health) {
    const message = `Session Health: ${health.status || 'Unknown'}`
    this.addActivityMessage(message, health.healthy ? 'success' : 'warning')
    
    if (health.details) {
      this.logger.debug('Session health details:', health.details)
    }
  }
}